import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Map,
  Plus,
  Trash2,
  Pencil,
  Info,
  Activity,
  CheckCircle,
  Construction,
  AlertCircle,
  X,
  Eye,
} from "lucide-react";
import MasterLayout from "../Sections/MasterLayout";
import {
  server_post_data,
  get_line_by_id,
  get_all_routes_by_line,
  delete_route,
} from "../../ServiceConnection/serviceconnection";
import { handleLinkClick } from "../../CommonJquery/CommonJquery";

export default function StationRoutes() {
  const { line_id } = useParams();
  const navigate = useNavigate();
  const [route, setRouteS] = useState([]);
  const [Line, setLineData] = useState([]);
  const [stations, setstations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [editingRoute, setEditingRoute] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("");
  const city_id = "4";

  // Fetch line data
  useEffect(() => {
    getline(city_id);
  }, []);

  const getline = async (city_id) => {
    setIsLoading(true);
    let fd = new FormData();
    fd.append("city_id", city_id);
    fd.append("line_id", line_id);
    await server_post_data(get_line_by_id, fd)
      .then((Response) => {
        console.log("Line Response:", Response.data.data);
        setLineData(Response.data.data);
        setstations(Response.data.stations);
        getRoute();
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching line data:", error);
        setIsLoading(false);
      });
  };

  // Fetch route data and set initial active tab
  const getRoute = async () => {
    setIsLoading(true);
    let fd = new FormData();
    fd.append("city_id", city_id);
    fd.append("line_id", line_id);
    await server_post_data(get_all_routes_by_line, fd)
      .then((Response) => {
        console.log("Routes Response:", Response.data.data);
        setRouteS(Response.data.data);
        // Set activeTab to the first route's name after fetching
        if (Response.data.data.length > 0) {
          setActiveTab(Response.data.data[0].route_name);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching routes:", error);
        setIsLoading(false);
      });
  };

  // Status badge for route status
  const getStatusBadge = (status) => {
    const statusConfig = {
      Operational: {
        color: "bg-green-50 text-green-700 border-green-200",
        icon: <CheckCircle size={12} />,
      },
      "Under Construction": {
        color: "bg-orange-50 text-orange-700 border-orange-200",
        icon: <Construction size={12} />,
      },
      Maintenance: {
        color: "bg-yellow-50 text-yellow-700 border-yellow-200",
        icon: <AlertCircle size={12} />,
      },
    };

    const config = statusConfig[status] || statusConfig["Operational"];
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium border rounded ${config.color}`}
      >
        {config.icon}
        {status}
      </span>
    );
  };

  // Modal handlers
  const openModal = (type, route = null) => {
    setModalType(type);
    setEditingRoute(route);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType("");
    setEditingRoute(null);
  };

  const handleRouteAction = (action, route) => {
    switch (action) {
      case "edit":
        openModal("edit", route);
        break;
      case "delete":
        openModal("delete", route);
        break;
      case "info":
        openModal("info", route);
        break;
      default:
        break;
    }
  };

  const confirmDelete = async () => {
    if (editingRoute) {
      console.log("Deleting route:", editingRoute.route_name);
      console.log("Deleting route Data:", editingRoute.primary_id);

      setIsLoading(true);
      let fd = new FormData();
      fd.append("route_id", editingRoute.primary_id);
      await server_post_data(delete_route, fd);
      getline();
      closeModal();
    }
  };

  const currentRoute = route.find((r) => r.route_name === activeTab);

  // Delete Confirmation Modal
  const DeleteConfirmModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-11/12 max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-red-600">Confirm Delete</h2>
          <button
            onClick={closeModal}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>
        <div className="mb-6">
          <p className="text-gray-700">
            Are you sure you want to delete{" "}
            <strong>{editingRoute?.route_name}</strong>?
          </p>
          <p className="text-sm text-gray-500 mt-2">
            This action cannot be undone.
          </p>
        </div>
        <div className="flex justify-end gap-3">
          <button
            onClick={closeModal}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={confirmDelete}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Delete Route
          </button>
        </div>
      </div>
    </div>
  );

  // Info Modal
  const InfoModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-11/12 max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">
            {editingRoute?.route_name} - Detailed Information
          </h2>
          <button
            onClick={closeModal}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">
                Route Overview
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Status</span>
                  {getStatusBadge(editingRoute?.status)}
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Distance</span>
                  <span className="font-medium">
                    {editingRoute?.total_distance}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Stations</span>
                  <span className="font-medium">
                    {editingRoute?.station_sequence?.length || 0}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Route</span>
                  <span className="font-medium">
                    {editingRoute?.origin_station_name} →{" "}
                    {editingRoute?.terminate_station_name}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">
                Operating Schedule
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Operating Hours</span>
                  <span className="font-medium">
                    {editingRoute?.open_hour} - {editingRoute?.close_hour}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <MasterLayout>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 w-full flex flex-col justify-between mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 h-full">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-0">
                Route Configuration
              </h1>
              <p className="text-sm text-gray-600 mt-1 mb-0">
                Manage metro routes, interchanges and connections
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => openModal("map")}
                className="flex items-center justify-center rounded-md gap-2 px-4 py-2 btn-secoundary transition duration-200"
              >
                <Map className="h-5 w-5" />
                Route Map
              </button>
              <button
                onClick={() => handleLinkClick(`/add-route/${line_id}`)}
                className="flex items-center justify-center rounded-md gap-2 px-4 py-2 btn-primary transition duration-200"
              >
                <Plus className="h-5 w-5" />
                Add Route
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-4">
            <p>Loading routes...</p>
          </div>
        )}

        {/* Route Overview Cards */}
        {!isLoading && route.length > 0 && (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-6">
            {route.map((line, i) => (
              <div
                key={line.primary_id}
                className="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setActiveTab(line.route_name)}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-[var(--black)]">
                      {line.route_name}
                    </h3>
                    {getStatusBadge(line.status)}
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[var(--grey-text)]">Route</span>
                      <span className="font-medium">
                        {line.origin_station_name} →{" "}
                        {line.terminate_station_name}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[var(--grey-text)]">Stations</span>
                      <span className="font-medium">
                        {line.station_sequence.length}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[var(--grey-text)]">Length</span>
                      <span className="font-medium">{line.total_distance}</span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveTab(line.route_name);
                      }}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium rounded-md btn-primary transition-colors"
                    >
                      <Eye size={14} />
                      View Details
                    </button>
                    <button
                      onClick={() =>
                        navigate(`/edit-route/${line_id}/${line.primary_id}`)
                      }
                      className="p-2 text-gray-400 hover:text-gray-600 border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRouteAction("info", line);
                      }}
                      className="p-2 text-gray-400 hover:text-blue-600 border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                    >
                      <Info size={14} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRouteAction("delete", line);
                      }}
                      className="p-2 text-gray-400 hover:text-red-600 border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && route.length === 0 && (
          <div className="text-center py-4">
            <p>No routes found for this line.</p>
          </div>
        )}

        {/* Tab Navigation */}
        {!isLoading && route.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="flex border-b border-gray-200">
              {route.map((tab) => (
                <button
                  key={tab.route_name}
                  onClick={() => setActiveTab(tab.route_name)}
                  className={`flex-1 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.route_name
                      ? "border-blue-500 text-blue-600 bg-blue-50"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {tab.route_name}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Station List */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Station Sequence
                    </h3>
                    <span className="text-sm text-gray-500">
                      {currentRoute?.station_sequence?.length || 0} stations
                    </span>
                  </div>

                  <div className="space-y-3">
                    {currentRoute?.station_sequence?.map((station, index) => (
                      <div
                        key={station.id}
                        className="flex items-center justify-between px-3 py-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-[var(--primary-color)] text-white rounded-full flex items-center justify-center text-sm font-medium">
                            {index + 1}
                          </div>
                          <div>
                            <div className="font-medium text-[var(--black)]">
                              {station.station_name}
                            </div>
                            <div className="text-sm text-[var(--grey-text)]">
                              {station.distance_from_previous} km from previous
                            </div>
                          </div>
                        </div>
                        {/* Uncomment if is_interchange is added to API */}
                        {/* {station.is_interchange && (
                          <span className="px-2 py-1 text-xs font-medium text-orange-700 bg-orange-100 rounded-full">
                            Interchange
                          </span>
                        )} */}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Route Information */}
                <div>
                  <div className="space-y-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                        <Activity size={16} />
                        Operating Details
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Distance</span>
                          <span className="font-medium">
                            {currentRoute?.total_distance}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Operating Hours</span>
                          <span className="font-medium">
                            {currentRoute?.open_hour} -{" "}
                            {currentRoute?.close_hour}
                          </span>
                        </div>
                        {currentRoute?.status === "Operational" && (
                          <>
                            {/* Add more operational details if available in API */}
                            {/* Example placeholders */}
                            <div className="flex justify-between">
                              <span className="text-gray-600">Frequency</span>
                              <span className="font-medium">N/A</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">
                                Daily Ridership
                              </span>
                              <span className="font-medium">N/A</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {showModal && modalType === "delete" && <DeleteConfirmModal />}
        {showModal && modalType === "info" && <InfoModal />}
      </div>
    </MasterLayout>
  );
}
